import { NetworkType } from '@mls-wallet/shared-types';
import { parseUri, getSdkError } from '@walletconnect/utils';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  UseWalletConnectResult,
  WalletConnectDappData,
  WalletConnectTransactionData,
  WalletConnectTransactionError,
  WalletConnectSessionRequestEvent,
} from './types';
import { walletConnectRuntime } from './walletConnectRuntime';

/**
 * Maintains WalletConnect UI state for the transaction builder.
 *
 * Design choice:
 * - `walletConnectRuntime` owns protocol lifecycle, client creation, and listeners.
 * - This hook only adapts runtime callbacks/events into React state and exposes
 *   UI-friendly action handlers through context.
 */
export function useWalletConnectController(
  accountAddress: string | undefined,
  networkId: NetworkType | undefined
): UseWalletConnectResult {
  const [dappData, setDappData] = useState<WalletConnectDappData[]>([]);
  const [transactionData, setTransactionData] =
    useState<WalletConnectTransactionData | null>(null);
  const [transactionError, setTransactionError] =
    useState<WalletConnectTransactionError | null>(null);
  const [transactionRequest, setTransactionRequest] =
    useState<WalletConnectSessionRequestEvent | null>(null);
  const [activeRequestTopic, setActiveRequestTopic] = useState<string | null>(
    null
  );
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    // No wallet context means no active sessions should be shown in UI state.
    if (!accountAddress || !networkId) {
      setDappData([]);
      setTransactionRequest(null);
      setActiveRequestTopic(null);
      setTransactionData(null);
      setTransactionError(null);
      setIsConnecting(false);
      return;
    }

    // Reset request/dialog state when switching account or network contexts.
    setDappData([]);
    setTransactionRequest(null);
    setActiveRequestTopic(null);
    setTransactionData(null);
    setTransactionError(null);
    setIsConnecting(false);

    let isCancelled = false;

    const setupClient = async () => {
      // Re-initialize runtime context whenever account/network changes so listener
      // callbacks always point to the latest React state setters.
      await walletConnectRuntime.initialize({
        accountAddress,
        networkId,
        onSessionProposal: (connectedDapps) => {
          if (isCancelled) return;
          setDappData(connectedDapps);
          // End "connecting" only when a session proposal is actually handled.
          setIsConnecting(false);
        },
        onSessionRequest: (request, requestedTransactionData, requestError) => {
          if (isCancelled) return;
          setTransactionRequest({ id: request.id, topic: request.topic });
          setActiveRequestTopic(request.topic);
          setTransactionData(
            requestedTransactionData
              ? { ...requestedTransactionData, topic: request.topic }
              : null
          );
          setTransactionError(requestError ?? null);
        },
        onSessionDelete: (connectedDapps) => {
          if (isCancelled) return;
          setDappData(connectedDapps);
        },
      });

      if (isCancelled) {
        return;
      }

      // Prune sessions that have exceeded the inactivity TTL.
      await walletConnectRuntime.pruneExpiredSessions();

      const existingConnections =
        await walletConnectRuntime.getExistingDappData();
      if (isCancelled) {
        return;
      }

      setDappData(existingConnections);
    };

    setupClient().catch(() => {
      if (isCancelled) return;
    });

    return () => {
      isCancelled = true;
    };
  }, [accountAddress, networkId]);

  const clearTransactionData = useCallback(() => {
    // Reset local import dialog state after approve/reject/cancel.
    setTransactionRequest(null);
    setActiveRequestTopic(null);
    setTransactionData(null);
    setTransactionError(null);
  }, []);

  const connectToDapp = useCallback(async (uri: string) => {
    try {
      // Validate URI up front so the sheet can surface parsing errors immediately.
      parseUri(uri);
      setIsConnecting(true);
      // Thin runtime wrapper: provider consumers call this action, runtime does pairing.
      await walletConnectRuntime.pair(uri);
    } catch (error) {
      setIsConnecting(false);
      throw error;
    }
  }, []);

  const cancelPendingConnection = useCallback(() => {
    // Cancel only affects local waiting UI; WalletConnect has no direct pair-abort API.
    setIsConnecting(false);
  }, []);

  const disconnect = useCallback(async (topic: string) => {
    // Thin runtime wrapper: disconnect via runtime, then refresh local dApp list.
    await walletConnectRuntime.disconnect(topic);
    const existingConnections =
      await walletConnectRuntime.getExistingDappData();
    setDappData(existingConnections);
  }, []);

  const rejectTransactionRequest = useCallback(async () => {
    if (!transactionRequest) return;
    try {
      // Thin runtime wrapper: send WalletConnect JSON-RPC error response.
      await walletConnectRuntime.respondError(
        transactionRequest,
        getSdkError('USER_REJECTED').message
      );
      clearTransactionData();
    } catch (error) {
      console.error('WalletConnect rejectTransactionRequest failed', {
        requestId: transactionRequest.id,
        topic: transactionRequest.topic,
        error,
      });
      throw error;
    }
  }, [transactionRequest, clearTransactionData]);

  const approveTransactionRequest = useCallback(async () => {
    if (!transactionRequest) return;
    try {
      // Thin runtime wrapper: send WalletConnect JSON-RPC success response.
      await walletConnectRuntime.respondSuccess(transactionRequest, '0x');
      clearTransactionData();
    } catch (error) {
      console.error('WalletConnect approveTransactionRequest failed', {
        requestId: transactionRequest.id,
        topic: transactionRequest.topic,
        error,
      });
      throw error;
    }
  }, [transactionRequest, clearTransactionData]);

  const walletConnectResult = useMemo(
    () => ({
      dappData,
      transactionData,
      transactionError,
      activeRequestTopic,
      isConnecting,
      connectToDapp,
      cancelPendingConnection,
      disconnect,
      approveTransactionRequest,
      rejectTransactionRequest,
      clearTransactionData,
    }),
    [
      dappData,
      transactionData,
      transactionError,
      activeRequestTopic,
      isConnecting,
      connectToDapp,
      cancelPendingConnection,
      disconnect,
      approveTransactionRequest,
      rejectTransactionRequest,
      clearTransactionData,
    ]
  );

  return walletConnectResult;
}
